import 'reflect-metadata';
import 'core-js/stable/array/at';
import { createRoot } from 'react-dom/client';
import { FeatureFlagProvider } from '@hub/providers';
import {
  configureApiClient,
  ReactQueryClientProvider,
} from '@monorepo/hub-common';
import { validateFrontendEnv } from '@monorepo/shared-env';
import { LanguageOption } from '@monorepo/types';
import App from './app';
import { DescopeAuthProvider } from './components/descope-auth-provider';
import { envSchema } from './env.schema';
import createStore from './helpers/create-store';
import { StoreProvider } from './helpers/store-provider';
import { EnvService } from './services/core/env-service';
import { initializeThirdPartyServices } from './utils/third-party-initializers';

import './index.scss';
import 'antd/dist/reset.css';
import 'rc-tooltip/assets/bootstrap_white.css';

const VALID_LANGUAGES = new Set(Object.values(LanguageOption));

const isValidLanguage = (code: string): boolean =>
  VALID_LANGUAGES.has(code as LanguageOption);

const getPreferredLanguage = (): string => {
  const storedLang = localStorage.getItem('i18nextLng');
  if (storedLang && isValidLanguage(storedLang)) {
    return storedLang;
  }

  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const potentialLang = pathSegments[0];
  return isValidLanguage(potentialLang) ? potentialLang : 'en';
};

const getLanguageFromUrl = (): string => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const potentialLang = pathSegments[0];
  return isValidLanguage(potentialLang) ? potentialLang : 'en';
};

// Validate environment variables at startup
validateFrontendEnv(envSchema, import.meta.env, 'hub');

const getRootElement = (): HTMLElement => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  return rootElement;
};

const renderApp = (
  rootStore: ReturnType<typeof createStore>['rootStore'],
  language: string,
) => {
  const rootElement = getRootElement();
  const reactRoot = createRoot(rootElement);
  const { isProduction } = EnvService.get();

  const appContent = (
    <FeatureFlagProvider>
      <StoreProvider value={rootStore}>
        <ReactQueryClientProvider showDevtools={!isProduction}>
          <DescopeAuthProvider>
            <App basename={language} />
          </DescopeAuthProvider>
        </ReactQueryClientProvider>
      </StoreProvider>
    </FeatureFlagProvider>
  );

  reactRoot.render(appContent);
};

const bootstrapApplication = async () => {
  try {
    const envConfig = EnvService.get();

    // Check if URL language matches preferred language
    const preferredLanguage = getPreferredLanguage();
    const urlLanguage = getLanguageFromUrl();

    // Redirect if they don't match
    if (urlLanguage !== preferredLanguage) {
      const pathWithoutLang = window.location.pathname.replace(
        /^\/[a-z]{2}/,
        '',
      );
      window.location.href = `/${preferredLanguage}${pathWithoutLang}`;
      return; // Stop initialization, browser will reload
    }

    // Configure the hub-common API client with the API endpoint
    configureApiClient(envConfig.apiEndPoint);

    initializeThirdPartyServices(envConfig);

    const { rootStore } = createStore({ envConfig });
    rootStore.themeStore.initTheme();

    renderApp(rootStore, preferredLanguage);
  } catch (error) {
    console.error('Application initialization failed:', error);
  }
};

void bootstrapApplication();
